import {Button} from '@ds/components';
import {DetailPageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  deleteOrPauseDeal,
  getDealCategories,
  getInProgressCustomDeals
} from 'store/slices/custom-deal';

import {CUSTOM_DEAL_ROUTES} from '../constants';
import {getCustomDealVendorId} from '../utils';
import DealDetailActionModal from './components/DealDetailActionModal/DealDetailActionModal';
import DealDetailInfo from './components/DealDetailInfo/DealDetailInfo';
import DealDetailProductsList from './components/DealDetailProductsList/DealDetailProductsList';

const Detail: React.FC = () => {
  const {dealId} = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {inProgressDeals} = useAppSelector((state) => state.customDeal);
  const vendorId = getCustomDealVendorId();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!inProgressDeals?.length) {
      void dispatch(getInProgressCustomDeals({vendorId}));
    }
    if (dealId) {
      void dispatch(getDealCategories({dealId, vendorId}));
    }
  }, [dispatch, vendorId, inProgressDeals?.length, dealId]);

  const deal = useMemo(() => {
    if (!dealId || !inProgressDeals?.length) {
      return null;
    }
    return inProgressDeals.find((item) => item.deal_id?.toString() === dealId.toString());
  }, [dealId, inProgressDeals]);

  const handleDeleteOrPauseDeal = async () => {
    if (!deal) {
      return;
    }

    try {
      await dispatch(
        deleteOrPauseDeal({
          dealId,
          vendorId
        })
      ).unwrap();
      toast(
        deal.state === 'active' ? 'تخفیف شما با موفقیت متوقف شد.' : 'تخفیف شما با موفقیت حذف شد.'
      );
      navigate(CUSTOM_DEAL_ROUTES.list);
    } catch (error: unknown) {
      const requestError = error as {errors?: {message?: string}; message?: string};
      toast(
        requestError.errors?.message ||
          requestError.message ||
          'خطایی در برقراری ارتباط با سرور رخ داد'
      );
    }
  };

  if (!deal) {
    return (
      <DetailPageLayout>
        <div className="flex flex-1 items-center justify-center p-8 text-body-sm text-neutral-fg">
          <span>تخفیف مورد نظر یافت نشد</span>
        </div>
      </DetailPageLayout>
    );
  }

  return (
    <>
      <DetailPageLayout
        footer={
          deal.state === 'initiated' ? (
            <div className="flex gap-2">
              <Button variant="outline" size="lg" fullWidth onClick={() => setShowConfirmModal(true)}>
                حذف تخفیف
              </Button>
              <Button
                fullWidth
                size="lg"
                onClick={() =>
                  navigate(
                    `${CUSTOM_DEAL_ROUTES.applyDiscount.replace(':dealId', String(dealId))}?isEditing=true`
                  )
                }
              >
                ویرایش تخفیف
              </Button>
            </div>
          ) : undefined
        }
      >
        <DealDetailInfo deal={deal} />
        <div className="flex items-center justify-between bg-white p-4">
          <h4 className="m-0 text-subtitle-md text-neutral-fg">کالا‌های شامل تخفیف</h4>
          <span className="text-body-sm text-neutral-fg">
            {deal.stock_availability?.product_count} کالا
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">
          <DealDetailProductsList dealId={dealId} />
        </div>
      </DetailPageLayout>
      <DealDetailActionModal
        isOpen={showConfirmModal}
        isActive={deal.state === 'active'}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          void handleDeleteOrPauseDeal();
        }}
      />
    </>
  );
};

export default Detail;
